// Moteur de recommandation v3 — Schéma Jolie Fille.
//
// Score final = Score_morpho × 0.5 + Score_maintien × 0.3 + Score_préférence × 0.2
//
// Sizing : chaque modèle a sa logique propre (Q4 direct, bande EU, tableau croisé...).
// Hauts : +1 taille (taillent petit) ; Top Palmyre : fourchette +1/+2 (S → entre M et L).
// Notes qualitatives bonnet : Palmyre/Marianne A/B → taille en dessous, Marianne DD+ → maintien généreux, Nicole DD+ → redirection.

import {
  MARIANNE_TABLE,
  PALMYRE_BAND_BASE,
  type Coverage,
  type MorphoCode,
  type Product,
  type Support,
} from '../catalog/products';

const TOP_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL'];
const BOT_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL'];

/**
 * Hauts (hors une-pièce) : les modèles taillent petit — on recommande UNE
 * taille au-dessus du calcul (XS→S, S→M…), plafonné à XL. Les hauts
 * n'existant qu'en XS–XL, un résultat XXS devient XS.
 */
function bumpTopSize(size: string): string {
  const i = TOP_SIZES.indexOf(size);
  if (i === -1) return size; // '—' ou inconnu : inchangé
  const bumped = TOP_SIZES[Math.min(i + 1, TOP_SIZES.length - 1)] ?? size;
  return bumped === 'XXS' ? 'XS' : bumped;
}

function bumpTop(r: { size: string; note?: string; alternative?: string }): { size: string; note?: string; alternative?: string } {
  return {
    size: bumpTopSize(r.size),
    alternative: r.alternative ? bumpTopSize(r.alternative) : undefined,
    note: r.note ?? (r.size !== '—' ? 'ce modèle taille petit → taille au-dessus' : undefined),
  };
}

type SizeResult = { size: string; note?: string; alternative?: string; between?: boolean };

/**
 * Top Palmyre : taille encore plus petit que les autres hauts — on situe la
 * cliente ENTRE la taille au-dessus et la suivante (S → entre M et L).
 * Plafonné à XL : une base L ou XL donne XL seul.
 */
function bumpPalmyre(size: string, note?: string): SizeResult {
  if (TOP_SIZES.indexOf(size) === -1) return { size }; // '—' ou inconnu : inchangé
  const low = bumpTopSize(size);
  const high = bumpTopSize(low);
  if (high === low) return { size: low, note: note ?? 'ce modèle taille petit → taille au-dessus' };
  return { size: low, alternative: high, between: true, note: note ?? 'ce modèle taille petit → entre deux tailles au-dessus' };
}

export interface RecommendationPrefs {
  q1Type?: 'two_pieces' | 'one_piece' | 'both';
  topSize?: string;
  bottomSize?: string;
  band?: string; // ex '85'
  cup?: string;  // ex 'C' ou 'DD_E'
  support?: Support;
  coverage?: Coverage;
  bigCup?: boolean;   // DD/E, F, G+
  smallCup?: boolean; // A, B
  fit?: 'tight' | 'loose' | 'depends';
  /** Mode d'intégration. En 'product' on ne renvoie pas de catégories. */
  mode?: 'home' | 'product';
}

export interface MatchBadge {
  label: 'Match parfait' | 'Très adapté' | 'Compatible' | 'À éviter';
  level: 'high' | 'medium' | 'low';
}

/**
 * Jauge visuelle de taille — montre où la cliente se situe entre 2 tailles
 * sur l'échelle complète du modèle. Plus parlant qu'une taille seule.
 */
export interface SizeGauge {
  /** Échelle complète du modèle, ex: ['XS', 'S', 'M', 'L', 'XL'] */
  scale: string[];
  /** Taille principale recommandée (mise en évidence). */
  primary: string;
  /** Taille alternative (cas "entre deux tailles"). Vide si certitude. */
  alternative?: string;
  /** Phrase courte affichée sous la jauge ("Plutôt M", "Entre S et M"...). */
  hint?: string;
}

export interface ScoredProduct {
  product: Product;
  matchScore: number; // 0-5
  badge: MatchBadge;
  recommendedSize: string;
  /** Libellé affichable : la taille seule, ou "entre M et L" (Top Palmyre). */
  recommendedSizeLabel: string;
  sizeNote?: string;
  /** Jauge visuelle (nouvelle v3.1). */
  sizeGauge?: SizeGauge;
  /** Notes qualitatives (ex: "Bonnet généreux → maintien renforcé") */
  cupNote?: string;
  reason: string;
}

export interface CategorizedRecommendations {
  tops: ScoredProduct[];
  bottoms: ScoredProduct[];
  onepiece: ScoredProduct[];
  showOnepiece: boolean;
  showTops: boolean;
  showBottoms: boolean;
  /** Message d'explication affiché si on a basculé en fallback (ex: 1pcs pas adapté → 2pcs proposés). */
  fallbackHint?: string;
}

// ============================================================
// Fonction publique
// ============================================================
export function recommend(
  morpho: MorphoCode,
  prefs: RecommendationPrefs,
  catalog: Product[],
): CategorizedRecommendations {
  const scored = catalog
    .filter((p) => p.morphos[morpho] > 0) // skip "à éviter" (score 0)
    .map((p) => scoreProduct(p, morpho, prefs));

  const tops = scored.filter((s) => s.product.category === 'top').sort((a, b) => b.matchScore - a.matchScore);
  const bottoms = scored.filter((s) => s.product.category === 'bottom').sort((a, b) => b.matchScore - a.matchScore);
  const onepiece = scored.filter((s) => s.product.category === 'onepiece').sort((a, b) => b.matchScore - a.matchScore);

  // Filtrage selon Q1 (type maillot)
  const showTops = prefs.q1Type !== 'one_piece';
  const showBottoms = prefs.q1Type !== 'one_piece';
  const showOnepiece =
    prefs.q1Type === 'one_piece' ||
    prefs.q1Type === 'both' ||
    (!prefs.q1Type && (morpho === 'X' || morpho === '8' || morpho === 'A'));

  return { tops, bottoms, onepiece, showOnepiece, showTops, showBottoms };
}

// ============================================================
// Scoring d'un produit
// ============================================================
function scoreProduct(p: Product, morpho: MorphoCode, prefs: RecommendationPrefs): ScoredProduct {
  // 1. Score morpho (note 0-5 du PPT) × 0.5
  const morphoScore = (p.morphos[morpho] ?? 0) * 0.5;

  // 2. Score maintien × 0.3
  let supportScore = 0;
  if (prefs.support && p.supportMatch) {
    supportScore = p.supportMatch.includes(prefs.support) ? 5 : 2;
  } else if (p.support) {
    supportScore = 3;
  }
  supportScore *= 0.3;

  // 3. Score préférence (couvrance, fit, etc.) × 0.2
  let prefScore = 0;
  if (p.category === 'bottom' && prefs.coverage && p.coverageMatch) {
    prefScore += p.coverageMatch.includes(prefs.coverage) ? 5 : 2;
  } else {
    prefScore += 3;
  }
  prefScore *= 0.2;

  // Ajustements couvrance (slide 7 PPT) — pénalité si bottom et couvrance contraire
  let coverageAdjust = 0;
  if (p.category === 'bottom' && prefs.coverage) {
    coverageAdjust = computeCoverageAdjust(p.id, prefs.coverage);
  }

  // Pénalité forte poitrine + tops à maintien léger
  if (prefs.bigCup && p.category === 'top' && p.support === 'light') {
    supportScore -= 0.5;
  }

  const total = Math.max(0, Math.min(5, morphoScore + supportScore + prefScore + coverageAdjust));

  // Taille recommandée selon la logique du modèle
  const sized = computeSize(p, prefs);

  // Notes qualitatives bonnet
  const cupNote = computeCupNote(p.id, prefs.cup);

  // Jauge visuelle (entre deux tailles si pertinent)
  const sizeGauge = buildSizeGauge(p, sized.size, sized.alternative, sized.note, sized.between);

  return {
    product: p,
    matchScore: total,
    badge: scoreToBadge(total),
    recommendedSize: sized.size,
    recommendedSizeLabel: sized.between && sized.alternative
      ? `entre ${sized.size} et ${sized.alternative}`
      : sized.size,
    sizeNote: sized.note,
    sizeGauge,
    cupNote,
    reason: buildReason(p, morpho),
  };
}

/** Échelle de tailles utilisée pour la jauge visuelle, selon la catégorie :
 *  hauts et une-pièce XS–XL, bas XXS–XL (gammes réelles Jolie Fille). */
function scaleFor(p: Product): string[] {
  if (p.category === 'bottom') return ['XXS', 'XS', 'S', 'M', 'L', 'XL'];
  return ['XS', 'S', 'M', 'L', 'XL'];
}

function buildSizeGauge(p: Product, primary: string, alternative?: string, note?: string, between?: boolean): SizeGauge | undefined {
  if (!primary || primary === '—') return undefined;
  const scale = scaleFor(p);
  // Hint synthétique selon ce qu'on a
  let hint = '';
  if (between && alternative && alternative !== primary) {
    hint = `Entre ${primary} et ${alternative}`;
    if (note?.includes('écart')) hint += ' · ⚠ écart avec ta taille habituelle, essaie en boutique';
  } else if (alternative && alternative !== primary) {
    hint = `Plutôt ${primary}, mais ${alternative} possible`;
  } else if (note?.includes('ajusté')) {
    hint = 'Préférence ajusté';
  } else if (note?.includes('ample')) {
    hint = 'Préférence à l\'aise';
  } else if (note?.includes('bande')) {
    hint = 'Calculé depuis ta bande SG';
  } else if (note?.includes('écart')) {
    hint = '⚠ Écart important — essaie en boutique';
  }
  return { scale, primary, alternative, hint: hint || undefined };
}

// ============================================================
// Calcul de taille selon la logique de chaque modèle
// ============================================================
function computeSize(p: Product, prefs: RecommendationPrefs): SizeResult {
  switch (p.sizingLogic) {
    case 'direct_top':
      return bumpTop({ size: prefs.topSize ?? '—' });

    case 'direct_bot':
      return { size: prefs.bottomSize ?? '—' };

    case 'palmyre_band': {
      // Bande EU → taille de base
      const base = prefs.band ? PALMYRE_BAND_BASE[prefs.band] : undefined;
      if (!base) return { size: '—' };
      // Si Q4 (top habituelle) diffère, on tranche + on garde l'alternative
      if (prefs.topSize) {
        const baseIdx = TOP_SIZES.indexOf(base);
        const topIdx = TOP_SIZES.indexOf(prefs.topSize);
        const diff = Math.abs(baseIdx - topIdx);
        if (diff === 0) return bumpPalmyre(base);
        if (diff === 1) {
          const lower = TOP_SIZES[Math.min(baseIdx, topIdx)] ?? base;
          const higher = TOP_SIZES[Math.max(baseIdx, topIdx)] ?? base;
          if (prefs.fit === 'tight') return bumpPalmyre(lower, 'préférence ajusté');
          if (prefs.fit === 'loose') return bumpPalmyre(higher, 'préférence à l\'aise');
          // Par défaut on prend la taille selon bande
          return bumpPalmyre(base, 'taille selon bande');
        }
        return bumpPalmyre(base, '⚠ écart important avec taille habituelle');
      }
      return bumpPalmyre(base);
    }

    case 'marianne_band_cup': {
      if (!prefs.band || !prefs.cup) return { size: '—' };
      const row = MARIANNE_TABLE[prefs.band];
      const size = row?.[prefs.cup];
      if (!size) return { size: '—' };
      return bumpTop({ size });
    }

    case 'nicole_band_bot': {
      // Bande EU → taille de base, croisée avec Q5 (taille bas)
      const base = prefs.band ? PALMYRE_BAND_BASE[prefs.band] : undefined;
      const bot = prefs.bottomSize;
      if (!base && !bot) return { size: '—' };
      if (!base) return { size: bot ?? '—' };
      if (!bot) return { size: base };
      // Croisé : prend la plus grande pour assurer la zone bas
      const baseIdx = TOP_SIZES.indexOf(base);
      const botIdx = BOT_SIZES.indexOf(bot);
      const baseInBot = BOT_SIZES.indexOf(base) !== -1 ? BOT_SIZES.indexOf(base) : Math.max(0, baseIdx - 1);
      const finalIdx = Math.max(baseInBot, botIdx);
      const finalSize = BOT_SIZES[finalIdx] ?? base;
      // Si bande et bas donnent des tailles différentes, garde l'alternative
      const alt = base !== bot ? (finalSize === base ? bot : base) : undefined;
      return { size: finalSize, note: 'taille bande × bas', alternative: alt };
    }
  }
}

// ============================================================
// Ajustement couvrance (slide 7 PPT)
// ============================================================
function computeCoverageAdjust(productId: string, coverage: Coverage): number {
  const TABLE: Record<string, Partial<Record<Coverage, number>>> = {
    'JF-BOT-ADELINA':  { minimal:  0, cheeky:  0, classique: -1, couvrant: -2 },
    'JF-BOT-PALMYRE':  { minimal:  0, cheeky:  0, classique: -1, couvrant: -2 },
    'JF-BOT-MARIANNE': { minimal: -1, cheeky:  0, classique:  0, couvrant:  0 },
  };
  return TABLE[productId]?.[coverage] ?? 0;
}

// ============================================================
// Notes qualitatives bonnet
// ============================================================
function computeCupNote(productId: string, cup?: string): string | undefined {
  if (!cup) return;
  const smallCup = cup === 'A' || cup === 'B';
  const bigCup = cup === 'DD_E' || cup === 'F' || cup === 'G_plus';

  if (smallCup && (productId === 'JF-TOP-PALMYRE' || productId === 'JF-TOP-MARIANNE')) {
    return 'Avec un petit bonnet, essaie une taille en dessous';
  }
  if (bigCup && productId === 'JF-TOP-MARIANNE') {
    return 'Bonnet généreux → maintien renforcé recommandé';
  }
  if (bigCup && productId === 'JF-1P-NICOLE') {
    return '⚠ Bonnet généreux : redirection vers les hauts (Palmyre / Marianne) conseillée';
  }
  return;
}

// ============================================================
// Badges
// ============================================================
function scoreToBadge(s: number): MatchBadge {
  if (s >= 4) return { label: 'Match parfait', level: 'high' };
  if (s >= 3) return { label: 'Très adapté', level: 'high' };
  if (s >= 1.5) return { label: 'Compatible', level: 'medium' };
  return { label: 'À éviter', level: 'low' };
}

function buildReason(p: Product, morpho: MorphoCode): string {
  const score = p.morphos[morpho] ?? 0;
  if (score >= 5) return `Idéal pour ta morphologie ${morpho}`;
  if (score >= 4) return `Très adapté à ta morphologie ${morpho}`;
  if (score >= 3) return 'Compatible selon tes préférences';
  return 'Possible selon ton envie';
}
